import React, { useState } from 'react'
import { Dayjs } from 'dayjs'
import {
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import Button from '@mui/material/Button'
import ClearIcon from '@mui/icons-material/Clear'
import DateRangePicker from '../../../components/DateRangePicker'
import ExportExcel from '../../../components/Export'
import CategorySelectSearch from '../../../components/SelectSearch/CategorySelectSearch'
import ProductGroupSelectSearch from '../../../components/SelectSearch/ProductGroupSelectSearch'
import { EPLATFORM } from '../../../constants/platform'

export interface IFilterMapped {
  page?: number
  perPage?: number
  search?: string
  startDate?: string
  endDate?: string
  categories?: string[]
  productGroups?: string[]
  inStock?: string
  platform?: string
}

interface FilterGroupProps {
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  onSearch: (filter: IFilterMapped) => void
  onDownload?: (date: Dayjs) => void
}

const IN_STOCK_OPTIONS = [
  { value: '', label: 'ทั้งหมด' },
  { value: 'true', label: 'มีสต็อก (จำนวนคงเหลือ > 0)' },
]

const PLATFORM_OPTIONS = [
  { value: '', label: 'ทุกช่องทาง' },
  { value: EPLATFORM.TIKTOK, label: 'TikTok' },
  { value: EPLATFORM.SHOPEE, label: 'Shopee' },
]

const ProductFilter = (props: FilterGroupProps) => {
  const { filter, onFilterChange, onSearch, onDownload } = props
  const { page, perPage, search, startDate, endDate, inStock, platform } =
    filter
  const [resetAutoComplete, setResetAutoComplete] = useState<string>('')

  return (
    <Grid container spacing={2} mb={2}>
      <Grid item xs={12} lg={6}>
        <TextField
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          label={'ชื่อสินค้า, รหัสสินค้า'}
          fullWidth
          id="search"
          name="search"
          variant="filled"
          value={search ?? ''}
          onChange={(e) =>
            onFilterChange({ ...filter, search: e.target.value })
          }
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <FormControl fullWidth variant="filled">
          <InputLabel id="inStock-label" shrink>
            จำนวนสินค้า
          </InputLabel>
          <Select
            labelId="inStock-label"
            id="inStock"
            value={inStock ?? ''}
            label="จำนวนสินค้า"
            displayEmpty
            onChange={(e) =>
              onFilterChange({
                ...filter,
                inStock: e.target.value ? String(e.target.value) : undefined,
              })
            }
          >
            {IN_STOCK_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={3}>
        <FormControl fullWidth variant="filled">
          <InputLabel id="platform-label" shrink>
            สินค้าแยกตาม Platform
          </InputLabel>
          <Select
            labelId="platform-label"
            id="platform"
            value={platform ?? ''}
            label="สินค้าแยกตาม Platform"
            displayEmpty
            onChange={(e) =>
              onFilterChange({
                ...filter,
                platform: e.target.value ? String(e.target.value) : undefined,
              })
            }
          >
            {PLATFORM_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={3}>
        <CategorySelectSearch
          resetAutoComplete={resetAutoComplete}
          categories={filter.categories ?? []}
          onSelect={(e) => {
            const selectedCategoryIds = e?.map(
              (category: any) => category?.value?.objectId,
            )
            onFilterChange({ ...filter, categories: selectedCategoryIds })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <ProductGroupSelectSearch
          resetAutoComplete={resetAutoComplete}
          productGroups={filter.productGroups}
          onSelect={(e) => {
            const selectedGroupIds = e?.map((group) => group.value.objectId)
            onFilterChange({ ...filter, productGroups: selectedGroupIds })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={(startDate, endDate) => {
            onFilterChange({ ...filter, startDate, endDate })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={2}>
        <Button
          color="primary"
          size="large"
          fullWidth
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => onSearch(filter)}
          sx={{ height: '100%' }}
        >
          ค้นหา
        </Button>
      </Grid>
      <Grid item xs={4} lg={2}>
        <Button
          color="inherit"
          size="large"
          fullWidth
          variant="contained"
          startIcon={<ClearIcon />}
          onClick={() => {
            const resetFilter = {
              page,
              perPage,
              search: undefined,
              startDate: undefined,
              endDate: undefined,
              inStock: undefined,
              platform: undefined,
              productGroups: undefined,
            }
            onFilterChange(resetFilter)
            onSearch(resetFilter)
            setResetAutoComplete(new Date().getTime().toString())
          }}
          sx={{ height: '100%' }}
        >
          รีเซ็ต
        </Button>
      </Grid>
      <Grid item xs={12} lg={2}>
        <ExportExcel onDownload={onDownload} />
      </Grid>
    </Grid>
  )
}

export default ProductFilter
